import styled from '@emotion/styled';
import { getWindowObject } from '@eventbrite/feature-detection';
import {
    Button,
    ChevronDownIcon,
    Divider,
    FlagIcon,
    Switch,
    TextField,
    Typography,
} from '@eventbrite/marmalade';
import React, { useEffect, useState } from 'react';
import { useAppContext } from '../AppContext';

export type ActionKeys =
    | 'create organization'
    | 'ai event creation'
    | 'create event'
    | 'create account'
    | 'publish event'
    | 'save onboarding responses';

type ErrorSimulationKeys =
    | 'shouldErrorOnCreateOrganization'
    | 'shouldErrorOnCreateTransientEvent'
    | 'shouldErrorOnCreateEvent'
    | 'shouldErrorOnCreateAccount'
    | 'shouldErrorOnPublishEvent'
    | 'shouldErrorOnSaveOnboardingResponses';

type DidErrorKeys =
    | 'didCreateOrganizationRequestFail'
    | 'didCreateTransientEventRequestFail'
    | 'didCreateEventRequestFail'
    | 'didCreateAccountRequestFail'
    | 'didPublishEventRequestFail'
    | 'didSaveOnboardingResponsesRequestFail';

type ThresholdKeys =
    | 'createOrganizationErrorThreshold'
    | 'createTransientErrorThreshold'
    | 'createEventErrorThreshold'
    | 'createAccountErrorThreshold'
    | 'publishEventErrorThreshold'
    | 'saveOnboardingResponsesErrorThreshold';

type ErrorCountKeys =
    | 'createOrganizationErrorCount'
    | 'createTransientEventErrorCount'
    | 'createEventErrorCount'
    | 'createAccountErrorCount'
    | 'publishEventErrorCount'
    | 'saveOnboardingResponsesErrorCount';

export const errorPropertyMap: Record<
    ActionKeys,
    {
        shouldError: ErrorSimulationKeys;
        threshold: ThresholdKeys;
        errorCount: ErrorCountKeys;
        didError: DidErrorKeys;
    }
> = {
    'create organization': {
        shouldError: 'shouldErrorOnCreateOrganization',
        threshold: 'createOrganizationErrorThreshold',
        errorCount: 'createOrganizationErrorCount',
        didError: 'didCreateOrganizationRequestFail',
    },
    'ai event creation': {
        shouldError: 'shouldErrorOnCreateTransientEvent',
        threshold: 'createTransientErrorThreshold',
        errorCount: 'createTransientEventErrorCount',
        didError: 'didCreateTransientEventRequestFail',
    },
    'create event': {
        shouldError: 'shouldErrorOnCreateEvent',
        threshold: 'createEventErrorThreshold',
        errorCount: 'createEventErrorCount',
        didError: 'didCreateEventRequestFail',
    },
    'create account': {
        shouldError: 'shouldErrorOnCreateAccount',
        threshold: 'createAccountErrorThreshold',
        errorCount: 'createAccountErrorCount',
        didError: 'didCreateAccountRequestFail',
    },
    'publish event': {
        shouldError: 'shouldErrorOnPublishEvent',
        threshold: 'publishEventErrorThreshold',
        errorCount: 'publishEventErrorCount',
        didError: 'didPublishEventRequestFail',
    },
    'save onboarding responses': {
        shouldError: 'shouldErrorOnSaveOnboardingResponses',
        threshold: 'saveOnboardingResponsesErrorThreshold',
        errorCount: 'saveOnboardingResponsesErrorCount',
        didError: 'didSaveOnboardingResponsesRequestFail',
    },
};

const ErrorOption = ({ action }: { action: ActionKeys }) => {
    const { errorSimulation, updateContext } = useAppContext();

    const { shouldError, threshold, errorCount } = errorPropertyMap[action];

    const [errorConfig, setErrorConfig] = useState({
        shouldError: errorSimulation[shouldError],
        threshold: '1',
        indefinitely: false,
    });

    // useEffect to update the config when the error we're simulating changes so
    // the toggle switches off when the error is no longer being simulated
    useEffect(() => {
        setErrorConfig((prev) => ({
            ...prev,
            shouldError: errorSimulation[shouldError],
        }));
    }, [errorSimulation, shouldError]);

    const handleThresholdChange = (value: string) => {
        setErrorConfig({
            ...errorConfig,
            threshold: value,
            indefinitely: false,
        });

        const parsedNumber = parseInt(value);

        updateContext({
            errorSimulation: {
                ...errorSimulation,
                [threshold]: isNaN(parsedNumber) ? 0 : parsedNumber,
                [shouldError]: errorConfig.shouldError,
            },
        });
    };

    const handleIndefinitelyChange = (checked: boolean) => {
        setErrorConfig({
            ...errorConfig,
            indefinitely: checked,
        });

        updateContext({
            errorSimulation: {
                ...errorSimulation,
                [threshold]: checked
                    ? Infinity
                    : parseInt(errorConfig.threshold),
                [shouldError]: errorConfig.shouldError,
            },
        });
    };

    const toggleErrorOnOrganizationCreation = (checked: boolean) => {
        setErrorConfig({
            ...errorConfig,
            shouldError: checked,
        });

        const thresholdValue = errorConfig.indefinitely
            ? Infinity
            : parseInt(errorConfig.threshold);

        updateContext({
            errorSimulation: {
                ...errorSimulation,
                [shouldError]: checked,
                [threshold]: checked ? thresholdValue : 0,
                [errorCount]: 0,
            },
        });
    };

    return (
        <TestOptionContainer>
            <TestOptionHeader>
                <Typography>{`Error on ${action}`}</Typography>
                <Switch
                    aria-label={`Should error on ${action} switch`}
                    onCheckedChange={toggleErrorOnOrganizationCreation}
                    checked={errorConfig.shouldError}
                />
            </TestOptionHeader>

            <TestParametersContainer>
                <Typography>Error limit</Typography>
                <ErrorCountTextField>
                    <TextField
                        label="Limit"
                        type="number"
                        value={errorConfig.threshold}
                        onChange={handleThresholdChange}
                        disabled={
                            !errorConfig.shouldError || errorConfig.indefinitely
                        }
                    />
                </ErrorCountTextField>
            </TestParametersContainer>

            <TestParametersContainer>
                <Typography>Indefinitely</Typography>
                <Switch
                    aria-label={`Should error on ${action} indefinitely switch`}
                    onCheckedChange={handleIndefinitelyChange}
                    checked={errorConfig.indefinitely}
                    disabled={!errorConfig.shouldError}
                />
            </TestParametersContainer>
        </TestOptionContainer>
    );
};

export const TestingPanel = () => {
    const {
        testingPanel: { isShowing },
        updateContext,
    } = useAppContext();

    if (getWindowObject('__SERVER_DATA__')?.env?.ebDomain !== 'evbqa.com') {
        return null;
    }

    return (
        <>
            {!isShowing ? (
                <ButtonContainer>
                    <Button
                        onClick={() =>
                            updateContext({ testingPanel: { isShowing: true } })
                        }
                    >
                        <FlagIcon color="#179c55" />
                    </Button>
                </ButtonContainer>
            ) : (
                <Container>
                    <HeaderContainer>
                        <Typography variant="heading-sm">
                            Error simulation
                        </Typography>
                        <Button
                            onClick={() =>
                                updateContext({
                                    testingPanel: { isShowing: false },
                                })
                            }
                        >
                            <ChevronDownIcon />
                        </Button>
                    </HeaderContainer>

                    <ErrorOption action="ai event creation" />
                    <Divider />
                    <ErrorOption action="create account" />
                    <Divider />
                    <ErrorOption action="create organization" />
                    <Divider />
                    <ErrorOption action="save onboarding responses" />
                    <Divider />
                    <ErrorOption action="create event" />
                    <Divider />
                    <ErrorOption action="publish event" />
                </Container>
            )}
        </>
    );
};

const Container = styled.div`
    width: 400px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    left: 16px;
    border-radius: 8px;
    background-color: #f9f9f9;
    box-shadow: 0px 4px 15px rgba(40, 44, 53, 0.06),
        0px 2px 2px rgba(40, 44, 53, 0.06);
    border: 1px solid #e0e0e0;
    z-index: 100;
`;

const ButtonContainer = styled.div`
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    left: 16px;
    z-index: 100;
`;

const HeaderContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
    padding-bottom: 24px;
`;

const TestOptionContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

const TestOptionHeader = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
`;

const TestParametersContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 16px;
`;

const ErrorCountTextField = styled.div`
    width: 120px;
`;
